import PageHeader from "@/components/common/PageHeader";
import { backgroundDbWorkRefetchIntervalMs } from "@/lib/background-db-work-pause";
import {
  monitoringService,
  type ErrorGroup,
  type ErrorSummary,
} from "@/services/monitoring.service";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import toast from "react-hot-toast";

const STATUS_BADGE: Record<string, string> = {
  open: "bg-red-50 text-red-600",
  regressed: "bg-orange-50 text-orange-600",
  resolved: "bg-green-50 text-green-600",
  ignored: "bg-slate-100 text-slate-500",
};

export default function ErrorTrackingPage() {
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState("open");
  const [platformFilter, setPlatformFilter] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const limit = 20;

  // Summary
  const { data: summary } = useQuery<ErrorSummary>({
    queryKey: ["errors", "summary"],
    queryFn: () => monitoringService.getErrorSummary(),
    refetchInterval: () => backgroundDbWorkRefetchIntervalMs(30_000),
  });

  // Error groups list
  const { data: errorData, isLoading } = useQuery<{
    data: ErrorGroup[];
    total: number;
  }>({
    queryKey: ["errors", statusFilter, platformFilter, search, page],
    queryFn: () =>
      monitoringService.getErrors({
        status: statusFilter || undefined,
        platform: platformFilter || undefined,
        search: search || undefined,
        page,
        limit,
        sortBy: "lastSeenAt",
        sortOrder: "desc",
      }),
  });

  const resolveMutation = useMutation({
    mutationFn: ({ id, note }: { id: string; note?: string }) =>
      monitoringService.resolveError(id, note),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["errors"] });
      toast.success("Error resolved");
    },
  });

  const ignoreMutation = useMutation({
    mutationFn: (id: string) => monitoringService.ignoreError(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["errors"] });
      toast.success("Error ignored");
    },
  });

  const reopenMutation = useMutation({
    mutationFn: (id: string) => monitoringService.reopenError(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["errors"] });
      toast.success("Error reopened");
    },
  });

  const totalPages = errorData ? Math.ceil(errorData.total / limit) : 0;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Error Tracking"
        subtitle="Monitor and resolve client-side errors across all tenants"
      />

      {/* Summary cards */}
      {summary && (
        <div className="grid gap-4 sm:grid-cols-4">
          <SummaryCard label="Open" value={summary.open} color="text-red-600" />
          <SummaryCard
            label="Regressed"
            value={summary.regressed}
            color="text-orange-600"
          />
          <SummaryCard
            label="Resolved"
            value={summary.resolved}
            color="text-green-600"
          />
          <SummaryCard
            label="New Today"
            value={summary.today}
            color="text-slate-900"
          />
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setPage(1);
          }}
          className="h-9 rounded-lg border border-slate-200 bg-white px-3 text-sm"
        >
          <option value="">All Statuses</option>
          <option value="open">Open</option>
          <option value="regressed">Regressed</option>
          <option value="resolved">Resolved</option>
          <option value="ignored">Ignored</option>
        </select>

        <select
          value={platformFilter}
          onChange={(e) => {
            setPlatformFilter(e.target.value);
            setPage(1);
          }}
          className="h-9 rounded-lg border border-slate-200 bg-white px-3 text-sm"
        >
          <option value="">All Platforms</option>
          <option value="web">Web</option>
          <option value="desktop">Desktop</option>
          <option value="mobile">Mobile</option>
        </select>

        <input
          type="text"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          placeholder="Search errors..."
          className="h-9 w-64 rounded-lg border border-slate-200 px-3 text-sm"
        />

        <span className="ml-auto text-xs text-slate-400">
          {errorData?.total ?? 0} total errors
        </span>
      </div>

      {/* Error list */}
      {isLoading ? (
        <p className="text-sm text-slate-500">Loading errors...</p>
      ) : errorData && errorData.data.length > 0 ? (
        <div className="space-y-3">
          {errorData.data.map((group) => (
            <div
              key={group.id}
              className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-slate-900">
                    {group.message}
                  </p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs font-semibold ${STATUS_BADGE[group.status] ?? "bg-slate-100 text-slate-500"}`}
                    >
                      {group.status}
                    </span>
                    <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-500">
                      {group.errorType}
                    </span>
                    <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-500">
                      {group.platform}
                    </span>
                    {group.source && (
                      <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-400">
                        {group.source}
                      </span>
                    )}
                  </div>
                  <p className="mt-2 text-xs text-slate-400">
                    First: {new Date(group.firstSeenAt).toLocaleDateString()} ·
                    Last: {new Date(group.lastSeenAt).toLocaleDateString()} ·
                    Tenant: {group.tenantId.slice(0, 8)}...
                  </p>
                </div>

                <div className="shrink-0 text-right">
                  <p className="text-2xl font-semibold text-slate-900">
                    {group.occurrenceCount}
                  </p>
                  <p className="text-xs text-slate-400">
                    {group.affectedUsers} users
                  </p>
                </div>
              </div>

              {/* Actions */}
              <div className="mt-3 flex items-center justify-end gap-2 border-t border-slate-100 pt-3">
                {group.status === "open" || group.status === "regressed" ? (
                  <>
                    <button
                      type="button"
                      disabled={resolveMutation.isPending}
                      onClick={() => resolveMutation.mutate({ id: group.id })}
                      className="rounded-lg bg-green-600 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-white transition hover:bg-green-700"
                    >
                      Resolve
                    </button>
                    <button
                      type="button"
                      disabled={ignoreMutation.isPending}
                      onClick={() => ignoreMutation.mutate(group.id)}
                      className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-slate-600 transition hover:bg-slate-50"
                    >
                      Ignore
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    disabled={reopenMutation.isPending}
                    onClick={() => reopenMutation.mutate(group.id)}
                    className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-slate-600 transition hover:bg-slate-50"
                  >
                    Reopen
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-slate-500">No errors found.</p>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
            className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 disabled:opacity-50"
          >
            Previous
          </button>
          <span className="text-xs text-slate-400">
            Page {page} of {totalPages}
          </span>
          <button
            type="button"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
            className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}

function SummaryCard({
  label,
  value,
  color,
}: {
  label: string;
  value: number;
  color: string;
}) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
        {label}
      </p>
      <p className={`mt-2 text-3xl font-semibold ${color}`}>{value}</p>
    </div>
  );
}
